import React, { useEffect, useRef, useState } from "react";
import { useSignalRConnectionStatus } from "prembid-shared-library-npm/signalr";
import { connect } from "react-redux";
import { PrembidLocalStorage } from "@/lib/utils/PrembidLocalStorage";
import { Person as PersonIcon, Business as BusinessIcon, Menu as MenuIcon } from '@mui/icons-material';
import {
    Button,
    Menu,
    Avatar,
    Badge,
    List,
    Divider,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Toolbar,
    IconButton,
    Drawer,
    Dialog,
    DialogTitle, DialogContent,
    Grid,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import useStyles from "./NavbarStyles";
import LoginRegisterModal from "@/components/Modal/LoginRegisterModal";
import { handleLogout, handleChangeProfile, toggleLoginRegisterModal } from "@/components/LoginForm/store/LoginForm";
import {
    FaGavel,
    FaHome,
    FaLockOpen,
    FaQuestion,
    FaBriefcase,
    FaBell,
    FaLock,
    FaUserTag
} from "react-icons/fa";
import {
    MdPerson,
    MdPeople
} from "react-icons/md";
import { MdArrowBack, MdArrowDropDown, MdClose } from "react-icons/md";
import { useLocation } from "react-router-dom";
import { BiddingProfileEnums } from '@/lib/utils/BiddingProfileEnums';
import { clearNotificationPanelState, retrieveNotificationCount, togglePanel as toggleNotificationPanel } from '@/components/NotificationPanel/store/NotificationPanel';
import { useSetProfileAsDefault } from "prembid-shared-library-npm/networking";
import { orderBy } from "lodash";
import { handleBidProfilesFetch } from "@/pages/BiddingProfile/store/BiddingProfile";
import { handleClearWishlistBiddingNotification } from "../../pages/MyActivity/store/MyActivity";
import useHistory from "../../lib/hooks/useHistory";
import { Authorized } from "../composite/Authorized";
import { ConnectionStatusIndicator } from "../composite/ConnectionStatusIndicator";
import { useConfirmationDialogActivator } from "../containers/ConfirmationDialogActivator";

interface Props {
    handleLogout_d: () => void,
    handleChangeProfile_d: (profileId: string) => any,
    loginSuccess: boolean,
    logoutSuccess: boolean,
    settings: any,
    bidProfiles: any,
    unreadNotificationCount: number,
    showNotificationPanel: () => void,
    refreshNotificationPanelCount: (profileId: string, onCompletedCallback: (response: any) => void) => void,
    handleBidProfileListFetch_d: () => void,
    handleClearWishlistBiddingNotification_d: (onCompletedCallback?: () => void) => void,
    clearNotificationPanelState_d: () => void,
    isShown: boolean,
    toggle: () => void,
    profileId: string,
    wishlistNotificationFlagObject: any;
}

const Navbar: React.FC<Props> = (props) => {
    const { handleLogout_d, handleChangeProfile_d, loginSuccess, logoutSuccess, settings, clearNotificationPanelState_d, bidProfiles, unreadNotificationCount, showNotificationPanel, refreshNotificationPanelCount, isShown, toggle, profileId, handleBidProfileListFetch_d, wishlistNotificationFlagObject, handleClearWishlistBiddingNotification_d } = props;

    const currentUser = PrembidLocalStorage.currentUser;
    const history = useHistory();
    const { classes } = useStyles();
    const [openMenu, setOpenMenu] = React.useState<null | HTMLElement>(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [anchorEl2, setAnchorEl2] = useState(null);
    const [currentProfileId, setCurrentProfileId] = React.useState<any>('');
    const [currentProfile, setCurrentProfile] = React.useState<any>('No Profiles');
    const [currentProfileList, setCurrentProfileList] = React.useState<any>([]);
    const [profileDialogOpen, setProfileDialogOpen] = React.useState(false);


    const { isLoading: isSettingProfileAsDefault, mutate: setProfileAsDefault } = useSetProfileAsDefault({
        onSuccess(data, { profileId }) {
            handleChangeProfile_d(profileId);
            PrembidLocalStorage.defaultProfile = profileId;
            handleBidProfileListFetch_d();
        }
    });

    const confirmationDialog = useConfirmationDialogActivator();

    const currentProfilePrefix = "Transacting as: ";

    const [state, setState] = useState({
        mobileView: false,
        drawerOpen: false,
        reloadAuctions: false
    });

    const { mobileView, drawerOpen } = state;
    const location = useLocation();
    // Phone-sized windows get the floating bottom tab bar (as in the mobile app).
    const [phoneView, setPhoneView] = useState(typeof window !== "undefined" && window.innerWidth < 768);
    useEffect(() => {
        const onResize = () => setPhoneView(window.innerWidth < 768);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const AlterBiddingProfilesToKeepDefaultFirst = (list) => {
        let tempArrayObjects = [...list];
        let targetIndex;
        let targetvalue;
        tempArrayObjects.map((x, index) => {
            if (x.isDefaultProfile === true) {
                targetIndex = index;
            }
        });

        if (targetIndex > -1) {
            targetvalue = tempArrayObjects[targetIndex];
            tempArrayObjects.splice(targetIndex, 1);
            tempArrayObjects.unshift(targetvalue);
        }
        return tempArrayObjects ?? []
    };

    // Tell sticky page bars (search bars, side panels) how tall the header is; it changes with the optional
    // website strip and the screen width.
    const headerRef = useRef<HTMLElement>(null);
    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        const publish = () => {
            const height = Math.round(el.getBoundingClientRect().height);
            document.documentElement.style.setProperty("--pb-header-h", height + "px");
            window.dispatchEvent(new CustomEvent("pb-header-resize", { detail: height }));
        };
        publish();
        const observer = new ResizeObserver(publish);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const setResponsiveness = () => {
            return window.innerWidth < 1150
                ? setState((prevState) => ({ ...prevState, mobileView: true }))
                : setState((prevState) => ({ ...prevState, mobileView: false }));
        };

        setResponsiveness();
        window.addEventListener("resize", () => setResponsiveness());
    }, []);

    useEffect(() => {
        if (profileId && currentProfileId !== profileId) {
            const existingProfile = currentProfileList?.find(x => x.profileId === profileId);
            if (existingProfile) {
                setCurrentProfile(currentProfilePrefix + existingProfile.profileName);
                setCurrentProfileId(existingProfile.profileId);
            }
        }
    }, [profileId])

    useEffect(() => {
        if (loginSuccess)
            setState((prevState) => ({ ...prevState, drawerOpen: false }));

        setAnchorEl(null);
        setAnchorEl2(null);

        let currentSessionStorageProfile = sessionStorage.getItem("current_user")

        if (currentUser && currentSessionStorageProfile) {

            let currentSessionStorageProfileObject = JSON.parse(currentSessionStorageProfile)

            if (currentUser.profiles && currentUser.profiles.length > 0) {
                setProfileDialogOpen(false);

                if (currentUser.profiles[0].profileId !== null) {
                    setCurrentProfileList(AlterBiddingProfilesToKeepDefaultFirst(currentUser.profiles))
                }

                if (currentUser.profiles[0].profileId !== null && currentSessionStorageProfileObject.currentProfileId === "") {
                    setCurrentProfileId(currentUser.profiles[0].profileId)
                    setCurrentProfile(currentProfilePrefix + currentUser.profiles[0].profileName)
                    setCurrentProfileList(AlterBiddingProfilesToKeepDefaultFirst(currentUser.profiles))
                }
            } else {

                if (window.location.pathname !== '/individualProfileAdd' && window.location.pathname !== '/companyProfileAdd') {
                    setProfileDialogOpen(true);
                }
            }
        }
    }, [loginSuccess]);

    React.useEffect(() => {
        if (bidProfiles) {
            var combinedProfiles: any[] = [];

            if (bidProfiles.iBiddingProfiles && bidProfiles.iBiddingProfiles.length > 0) {
                for (var i = 0; i < bidProfiles.iBiddingProfiles.length; i++) {
                    combinedProfiles.push({
                        profileId: bidProfiles.iBiddingProfiles[i].id,
                        profileName: bidProfiles.iBiddingProfiles[i].firstName + ' ' + bidProfiles.iBiddingProfiles[i].lastName,
                        isDefaultProfile: bidProfiles.iBiddingProfiles[i].isDefaultProfile
                    })
                }
            }

            if (bidProfiles.cBiddingProfiles && bidProfiles.cBiddingProfiles.length > 0) {
                for (var i = 0; i < bidProfiles.cBiddingProfiles.length; i++) {
                    combinedProfiles.push({
                        profileId: bidProfiles.cBiddingProfiles[i].id,
                        profileName: bidProfiles.cBiddingProfiles[i].companyName,
                        isDefaultProfile: bidProfiles.cBiddingProfiles[i].isDefaultProfile
                    })
                }
            }

            if (combinedProfiles && combinedProfiles.length > 0) {
                var orderedListIList = orderBy(combinedProfiles ?? [], ['profileName'], ['asc']);
                setCurrentProfileList(AlterBiddingProfilesToKeepDefaultFirst(orderedListIList))
                if (combinedProfiles.length === 1) {
                    setCurrentProfile(currentProfilePrefix + combinedProfiles[0].profileName)
                    handleChangeProfile(combinedProfiles[0].profileId);
                }
            } else {
                setCurrentProfileList(AlterBiddingProfilesToKeepDefaultFirst(combinedProfiles))
                setCurrentProfile('No Profiles')

                handleChangeProfile(undefined);
            }


        }
    }, [bidProfiles])

    useEffect(() => {
        let currentSessionStorageProfile = sessionStorage.getItem("current_user")
        if (currentProfileList instanceof Array && currentProfileList.length >= 1 && currentSessionStorageProfile) {
            let currentSessionStorageProfileObject = JSON.parse(currentSessionStorageProfile)
            if (currentSessionStorageProfileObject.currentProfileId !== "") {
                let currentProfileObject = currentProfileList.filter(obj => {
                    return obj.profileId === currentSessionStorageProfileObject.currentProfileId
                })

                if (currentProfileObject[0] !== undefined) {
                    setCurrentProfile(currentProfilePrefix + currentProfileObject[0].profileName)
                    handleChangeProfile(currentProfileObject[0].profileId);
                }
            }

        }
    }, [currentProfileList])

    useEffect(() => {
        if (logoutSuccess) {
            setOpenMenu(null);
            clearNotificationPanelState_d();
        }
    }, [logoutSuccess]);

    useEffect(() => {
        if (profileId) refreshNotificationPanelCount(profileId, (response) => { });
    }, [profileId])

    // Back online after a dropped live connection: refresh the unread count (notifications that arrived while offline
    // were never counted), as on mobile.
    useSignalRConnectionStatus({
        onConnect: () => {
            if (profileId) refreshNotificationPanelCount(profileId, () => { });
        },
    });

    const onSetAsDefault = (id: string, profileName: string) => {
        confirmationDialog?.showConfirmationDialog({
            title: "Set as default profile",
            description: `Are you sure you want to set "${profileName}" as your default profile?`,
        }, {
            onOkPress() {
                setProfileAsDefault({ profileId: id })
                setAnchorEl2(null);
            },
        });
    }

    const handleChangeProfile = (profileId) => {
        handleChangeProfile_d(profileId);
    }

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleProfileClick = (event) => {
        setAnchorEl2(event.currentTarget);
    };

    const handleNotificationsClick = (event) => {
        showNotificationPanel();
    }

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleProfileClose = () => {
        setAnchorEl2(null);
    };

    const handleProfileMenuClick = (profileId: string, profileName: string) => {
        handleChangeProfile(profileId)

        setCurrentProfileId(profileId)
        setCurrentProfile(currentProfilePrefix + profileName)
        setAnchorEl2(null);
    };

    const navigateProfiles = (path: string) => {
        setProfileDialogOpen(false);
        history.push(path)
        setState((prevState) => ({ ...prevState, drawerOpen: false }));
    }

    const navigateAuctions = () => {
        let path = `/auctions`;
        history.push(path, { state });
        setState((prevState) => ({ ...prevState, drawerOpen: false, reloadAuctions: true }));
    };

    const navigateMyActivity = () => {
        handleClearWishlistBiddingNotification_d(() => {
            let path = `/myActivity`;
            history.push(path, { state });
            setState((prevState) => ({ ...prevState, drawerOpen: false }));
        })

        let path = `/myActivity`;
        history.push(path, { state });
        setState((prevState) => ({ ...prevState, drawerOpen: false }));

    };

    const navigateHome = () => {
        let path = `/home`;
        history.push(path);
        setState((prevState) => ({ ...prevState, drawerOpen: false }));
    };

    const navigateHowItWorks = () => {
        let path = `/howitworks`;
        history.push(path);
        setState((prevState) => ({ ...prevState, drawerOpen: false }));
    };

    const navigateRegister = () => {
        let path = `/register`;
        history.push(path);
        setState((prevState) => ({ ...prevState, drawerOpen: false }));
    };

    const navigateLogout = () => {
        handleClose();
        handleChangeProfile('');
        handleLogout_d();

        setCurrentProfileId('')
        setCurrentProfile('No Profiles')
        setCurrentProfileList([])

        if (window.location.pathname === '/individualProfileAdd' ||
            window.location.pathname === '/individualProfileEdit' ||
            window.location.pathname === '/companyProfileAdd' ||
            window.location.pathname === '/companyProfileEdit' ||
            window.location.pathname === '/account' ||
            window.location.pathname === '/biddingProfileList') {
            let path = `/home`;
            history.push(path);
        }
        setState((prevState) => ({ ...prevState, drawerOpen: false }));
    };

    const navigateProfile = () => {
        handleClose();

        let path = `/account`;
        history.push(path);
        setState((prevState) => ({ ...prevState, drawerOpen: false }));
    };

    const navigateBiddingProfile = () => {
        handleClose();

        let path = `/biddingProfileList`;
        history.push(path);
        setState((prevState) => ({ ...prevState, drawerOpen: false }));
    };

    const getAvatarInitial = () => {
        if (currentUser?.email)
            return currentUser?.email[0];
    };

    const VendorLogo = (
        <img
            src={settings.VendorLogoUrl}
            className={classes.logoImg}
            onClick={navigateHome}
            alt={settings.VendorName}
        />
    );

    const openVendorWebsite = () => {
        window.location.href = "https://" + settings.VendorWebsite;
    }

    const navTextColor = settings?.Styles?.OverrideNavBarTextColor || "";
    const brand = settings?.Styles?.OverrideNavBarCallToActionColor || settings?.Styles?.Primary;
    const activityDot = (wishlistNotificationFlagObject?.wishlistedActivity || wishlistNotificationFlagObject?.biddingActivityFlag) === true;
    const path = location.pathname.toLowerCase();
    const isActive = (key: string) =>
        key === "home" ? path.startsWith("/home")
            : key === "auctions" ? path.startsWith("/auctions") || path.startsWith("/lots") || path.startsWith("/lotbidandinfo")
                : key === "activity" ? path.startsWith("/myactivity")
                    : key === "how" ? path.startsWith("/howitworks")
                        : false;

    // One link in the top bar: text pill, tinted when it is the current section.
    const NavLink = ({ id, label, onClick, dot = false }: { id: string; label: string; onClick: () => void; dot?: boolean }) => (
        <button
            type="button"
            onClick={onClick}
            className={classes.navLink + (isActive(id) ? " " + classes.navLinkActive : "")}
            style={navTextColor ? { color: navTextColor } : undefined}
            aria-current={isActive(id) ? "page" : undefined}
        >
            {label}
            {dot && <span className={classes.dot} />}
        </button>
    );

    const ProfileChip = () => (
        <Authorized required={false}>
            <button type="button" className={classes.profileChip} onClick={handleProfileClick} title={currentProfile}>
                <span className={classes.profileChipText}>{currentProfile?.replace(currentProfilePrefix, "")}</span>
                <MdArrowDropDown size={22} />
            </button>
        </Authorized>
    );

    const BellButton = () => (
        <Authorized required={false}>
            <IconButton onClick={handleNotificationsClick} className={classes.iconButton} aria-label="Notifications">
                <Badge color="error" badgeContent={unreadNotificationCount} max={99}>
                    <FaBell size={18} />
                </Badge>
            </IconButton>
        </Authorized>
    );

    const AvatarMenu = () => (
        <Authorized required={false}>
            <div>
                <IconButton onClick={handleClick} className="p-1" aria-label="Account">
                    <Avatar
                        style={{ width: 38, height: 38, fontWeight: 700, background: settings?.Styles?.OverrideNavBarAvatarBackgroundColor || settings?.Styles?.Primary, color: settings?.Styles?.OverrideNavBarAvatarTextColor || "#ffffff" }}
                        alt="avatar"
                    >
                        {getAvatarInitial()?.toUpperCase()}
                    </Avatar>
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                >
                    <div className={classes.menuHeader}>
                        <div className={classes.menuTitle}>Signed in</div>
                        <div className={classes.menuSub}>{currentUser?.email}</div>
                    </div>
                    <Divider />
                    <List dense className="py-1">
                        <ListItem button onClick={navigateProfile}><MdPerson className="mr-2" size={18} /> My Account</ListItem>
                        <ListItem button onClick={navigateBiddingProfile}><MdPeople className="mr-2" size={18} /> My Profiles</ListItem>
                        {settings?.VendorWebsite && <ListItem button onClick={openVendorWebsite}><FaHome className="mr-2" size={15} /> {settings.VendorWebsite}</ListItem>}
                    </List>
                    <Divider />
                    <List dense className="py-1">
                        <ListItem button onClick={navigateLogout} style={{ color: "#E5484D" }}><FaLock className="mr-2" size={14} /> Log Out</ListItem>
                    </List>
                </Menu>
            </div>
        </Authorized>
    );

    const ProfilesMenu = () => (
        <Authorized required={false}>
            <Menu
                anchorEl={anchorEl2}
                keepMounted
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorEl2)}
                onClose={handleProfileClose}
            >
                <div className={classes.menuHeader} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div className={classes.menuTitle}>Transacting as</div>
                    {isSettingProfileAsDefault && <CircularProgress size={18} style={{ color: brand }} />}
                </div>
                <Divider />
                <BiddingProfiles />
            </Menu>
        </Authorized>
    );

    const LoginButton = ({ fullWidth = false }: { fullWidth?: boolean } = {}) => (
        <Authorized>
            <Button variant="contained" onClick={() => { setState((prevState) => ({ ...prevState, drawerOpen: false })); toggle(); }} fullWidth={fullWidth} style={{ background: brand, color: "#ffffff" }}>
                Log In / Sign Up
            </Button>
        </Authorized>
    );

    // Desktop: logo, links, then status, notifications, profile and account on the right.
    const displayDesktop = () => (
        <div className={classes.bar}>
            <div className={classes.logoWrap}>{VendorLogo}</div>
            <nav className={classes.links}>
                {NavLink({ id: "home", label: "Home", onClick: navigateHome })}
                {NavLink({ id: "auctions", label: settings.AuctionName + "s", onClick: navigateAuctions })}
                <Authorized required={false}>
                    {NavLink({ id: "activity", label: "My Activity", onClick: navigateMyActivity, dot: activityDot })}
                </Authorized>
                {NavLink({ id: "how", label: "How it Works", onClick: navigateHowItWorks })}
            </nav>
            <div className={classes.actions}>
                <ConnectionStatusIndicator />
                {BellButton()}
                {ProfileChip()}
                {AvatarMenu()}
                {LoginButton()}
            </div>
        </div>
    );

    // Tablet / narrow windows: logo and actions, the links move into a side panel.
    const displayMobile = () => {
        const handleDrawerOpen = () => setState((prevState) => ({ ...prevState, drawerOpen: !prevState.drawerOpen }));
        const handleDrawerClose = () => setState((prevState) => ({ ...prevState, drawerOpen: false }));

        return (
            <div className={classes.bar}>
                <div className={classes.logoWrap}>{VendorLogo}</div>
                <div className={classes.actions}>
                    <ConnectionStatusIndicator />
                    {BellButton()}
                    {!phoneView && ProfileChip()}
                    <IconButton onClick={handleDrawerOpen} className={classes.iconButton} aria-label="Menu" aria-haspopup="true">
                        <MenuIcon />
                    </IconButton>
                </div>

                <Drawer anchor="right" open={drawerOpen} onClose={handleDrawerClose} classes={{ paper: classes.sidePanel }}>
                    {getDrawerChoices()}
                </Drawer>
            </div>
        );
    };

    // Side panel contents (tablet and phone "More"): sections as a simple list.
    const getDrawerChoices = () => {
        const Item = ({ icon, label, onClick, active = false, danger = false, dot = false, badge = 0 }: any) => (
            <button type="button" onClick={onClick} className={classes.panelItem + (active ? " " + classes.panelItemActive : "")} style={danger ? { color: "#E5484D" } : undefined}>
                <span className={classes.panelIcon}>{icon}</span>
                <span style={{ flex: 1 }}>{label}</span>
                {dot && <span className={classes.dot} />}
                {badge > 0 && <span className={classes.countBadge}>{badge > 99 ? "99+" : badge}</span>}
            </button>
        );

        return (
            <div className={classes.panel}>
                <div className={classes.panelHeader}>
                    <div className={classes.logoWrap}>{VendorLogo}</div>
                    <IconButton onClick={() => setState((prevState) => ({ ...prevState, drawerOpen: false }))} aria-label="Close" className={classes.iconButton}>
                        <MdClose size={20} />
                    </IconButton>
                </div>

                <Authorized required={false}>
                    <div className={classes.panelSection}>
                        <div className={classes.panelLabel}>Transacting as</div>
                        <button type="button" className={classes.profileChip + " w-100 justify-content-between"} onClick={handleProfileClick}>
                            <span className={classes.profileChipText}>{currentProfile?.replace(currentProfilePrefix, "")}</span>
                            <MdArrowDropDown size={22} />
                        </button>
                    </div>
                </Authorized>

                <div className={classes.panelSection}>
                    {Item({ icon: <FaHome />, label: "Home", onClick: navigateHome, active: isActive("home") })}
                    {Item({ icon: <FaGavel />, label: settings.AuctionName + "s", onClick: navigateAuctions, active: isActive("auctions") })}
                    <Authorized required={false}>
                        {Item({ icon: <FaBriefcase />, label: "My Activity", onClick: navigateMyActivity, active: isActive("activity"), dot: activityDot })}
                    </Authorized>
                    {Item({ icon: <FaQuestion />, label: "How it Works", onClick: navigateHowItWorks, active: isActive("how") })}
                    <Authorized required={false}>
                        {Item({ icon: <FaBell />, label: "Notifications", onClick: handleNotificationsClick, badge: unreadNotificationCount })}
                    </Authorized>
                </div>

                <Authorized required={false}>
                    <div className={classes.panelSection}>
                        {Item({ icon: <MdPerson />, label: "My Account", onClick: navigateProfile })}
                        {Item({ icon: <MdPeople />, label: "My Profiles", onClick: navigateBiddingProfile })}
                        {Item({ icon: <FaLock />, label: "Log Out", onClick: navigateLogout, danger: true })}
                    </div>
                </Authorized>

                <div className={classes.panelSection}>
                    {LoginButton({ fullWidth: true })}
                </div>

                <div className={classes.panelFooter}>
                    <ConnectionStatusIndicator showText showBanner={false} />
                    {settings?.VendorWebsite && <a onClick={openVendorWebsite} className={classes.link}>Back to {settings.VendorWebsite}</a>}
                </div>
            </div>
        );
    };

    // Phone-sized windows: the floating tab bar from the mobile app.
    const BottomTabs = () => {
        const Tab = ({ id, icon, label, onClick, dot = false }: any) => (
            <button type="button" onClick={onClick} className={classes.tab + (isActive(id) ? " " + classes.tabActive : "")} aria-current={isActive(id) ? "page" : undefined}>
                <span className={classes.tabIcon}>{icon}{dot && <span className={classes.tabDot} />}</span>
                <span className={classes.tabLabel}>{label}</span>
            </button>
        );
        return (
            <nav className={classes.bottomBar} aria-label="Main">
                <div className={classes.bottomBarInner}>
                    {Tab({ id: "auctions", icon: <FaGavel />, label: settings.AuctionName + "s", onClick: navigateAuctions })}
                    <Authorized required={false}>
                        {Tab({ id: "activity", icon: <FaBriefcase />, label: "My Activity", onClick: navigateMyActivity, dot: activityDot })}
                    </Authorized>
                    {Tab({ id: "how", icon: <FaQuestion />, label: "How It Works", onClick: navigateHowItWorks })}
                    {Tab({ id: "more", icon: <MenuIcon fontSize="small" />, label: "More", onClick: () => setState((prevState) => ({ ...prevState, drawerOpen: true })) })}
                </div>
            </nav>
        );
    };

    function BiddingProfiles() {
        return (
            <div style={{ maxHeight: 400, overflowY: 'auto', overflowX: 'hidden' }}>
                <List
                >
                    {
                        currentProfileList.map((profile) => {
                            return <ListItem
                                key={profile.profileId}
                                button
                                className="d-block text-left"
                                style={{ width: '100%' }}
                                onClick={() => { handleProfileMenuClick(profile.profileId, profile.profileName) }}>
                                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                                    <div style={{ overflowWrap: 'anywhere', paddingRight: 10 }}>{profile.profileName}</div>

                                    {!profile.isDefaultProfile && <div style={{ width: 30 }}>
                                        <Tooltip arrow title={'Set as default'} PopperProps={{ style: { zIndex: 0 } }}>
                                            <Button onClick={(event) => {
                                                event.stopPropagation();
                                                onSetAsDefault(profile.profileId, profile.profileName);
                                            }} size="small" className="text-white btn-neutral-primary rounded-sm shadow-none hover-scale-sm d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center text-capitalize" style={{ background: '#2B2B2B' }}>
                                                <FaUserTag />
                                            </Button>
                                        </Tooltip>
                                    </div>}
                                </div>
                            </ListItem>
                        })
                    }

                </List>
            </div>
        );
    }

    return (
        <>
            {/* Sticky, in the page flow (no fixed offset for pages to clear). */}
            <header ref={headerRef} className={classes.header} style={{ background: settings?.Styles?.OverrideNavBarBackgroundColor || undefined }}>
                {settings?.VendorWebsite && (
                    <div className={classes.siteStrip}>
                        <div className={classes.siteStripInner}>
                            <a onClick={openVendorWebsite} className={classes.siteLink}>
                                <MdArrowBack size={14} /> Back to {settings.VendorWebsite}
                            </a>
                        </div>
                    </div>
                )}
                {mobileView ? displayMobile() : displayDesktop()}
            </header>
            {ProfilesMenu()}
            {phoneView && BottomTabs()}

            <LoginRegisterModal isShown={isShown} hide={toggle} isMobile={mobileView} />

            <Dialog onClose={handleClose} aria-labelledby="simple-dialog-title" open={profileDialogOpen}>
                <DialogTitle id="simple-dialog-title">Please set up a profile</DialogTitle>
                {/* Company / Individual as the two option cards used in the registration wizard. */}
                <DialogContent>
                    <div className="pb-reg-options">
                        <button type="button" className="pb-reg-option" onClick={() => navigateProfiles('/companyProfileAdd')}>
                            <span className="pb-reg-option-icon"><BusinessIcon /></span>
                            Company
                        </button>
                        <button type="button" className="pb-reg-option" onClick={() => navigateProfiles('/individualProfileAdd')}>
                            <span className="pb-reg-option-icon"><PersonIcon /></span>
                            Individual
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

const mapStateToProps = (state: any) => ({
    loginSuccess: state.loginForm.loginSuccess,
    logoutSuccess: state.loginForm.logoutSuccess,
    settings: state.settings.settings,
    bidProfiles: state.bidProfile.bidProfiles,
    unreadNotificationCount: state.notificationPanel.unreadNotificationCount,
    isShown: state.loginForm.showLoginRegisterModal,
    profileId: state.loginForm.changeProfileId,
    wishlistNotificationFlagObject: state.myActivity.wishlistNotificationFlagObject
});

const mapDispatchToProps = (dispatch: any) => {
    return {
        handleLogout_d: () => dispatch(handleLogout()),
        handleChangeProfile_d: (profileId: string) => dispatch(handleChangeProfile(profileId)),
        showNotificationPanel: () => dispatch(toggleNotificationPanel(true)),
        toggle: () => dispatch(toggleLoginRegisterModal()),
        handleBidProfileListFetch_d: () => dispatch(handleBidProfilesFetch()),
        refreshNotificationPanelCount: (profileId: string, onCompletedCallback: (response: any) => void) => dispatch(retrieveNotificationCount(profileId, false, onCompletedCallback)),
        handleClearWishlistBiddingNotification_d: (onCompletedCallback?: () => void) => dispatch(handleClearWishlistBiddingNotification(onCompletedCallback)),
        clearNotificationPanelState_d: () => dispatch(clearNotificationPanelState()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Navbar);
